'use client'

import { Link } from '~/components/common/Link'
import { cn } from '@nextui-org/react'
import { FaGithub } from '@react-icons/all-files/fa/FaGithub'
import { FaLinkedin } from '@react-icons/all-files/fa/FaLinkedin'
import { FaSlack } from '@react-icons/all-files/fa/FaSlack'
import type { FC } from 'react'
import IconLogo from '~/assets/logo.svg'

interface FooterProps {
  className?: string
}

export const Footer: FC<FooterProps> = ({ className }) => {
  return (
    <footer className={cn('w-full pt-12 border-t border-border', className)}>
      <div className='container mx-auto px-4 flex flex-col text-muted-foreground'>
        <div className='flex flex-col md:flex-row justify-between mb-12 gap-8'>
          {/* Logo and Social Section */}
          <div className='space-y-4'>
            <div className='inline-flex items-center rounded p-1'>
              <img src={IconLogo} alt='Curie Logo' width={80} height={40} draggable={false} />
            </div>

            {/* Social Links */}
            <div className='flex gap-4'>
              <Link
                href='https://github.com/curietech'
                className='text-muted-foreground hover:text-foreground transition-colors'
              >
                <FaGithub className='h-5 w-5' />
              </Link>
              <Link
                href='https://www.linkedin.com/company/curietech-inc'
                className='text-muted-foreground hover:text-foreground transition-colors'
              >
                <FaLinkedin className='h-5 w-5' />
              </Link>
              <Link
                href='https://join.slack.com/t/curietechcommunity/shared_invite/zt-2pjxi8r4h-MDgbb0DiEuLj22Z7KMnZ3w'
                className='text-muted-foreground hover:text-foreground transition-colors'
              >
                <FaSlack className='h-5 w-5' />
              </Link>
            </div>

            {/* Status Badge */}
            <Link
              href='https://curie.statuspage.io'
              className='inline-flex items-center gap-2 px-3 py-1 rounded-md border'
              target='_blank'
            >
              <div className='w-2 h-2 rounded-full bg-green-500' />
              <span className='text-sm'>System Status</span>
            </Link>
          </div>

          {/* Navigation Links */}
          <div className='grid grid-cols-2 md:grid-cols-3 gap-8'>
            {/* Product Links */}
            <div className='space-y-3'>
              <h2 className='font-semibold text-foreground'>Product</h2>
              <ul className='space-y-2'>
                <li>
                  <Link
                    href='https://www.curietech.ai'
                    target='_blank'
                    className='text-sm hover:text-foreground transition-colors'
                  >
                    Curie
                  </Link>
                </li>
                <li>
                  <Link
                    href='/test-drive'
                    target='_blank'
                    className='text-sm hover:text-foreground transition-colors'
                  >
                    Test Drive
                  </Link>
                </li>
                <li>
                  <Link
                    href='mailto:sales@curietech.net'
                    className='text-sm hover:text-foreground transition-colors'
                  >
                    Enterprise
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources Links */}
            <div className='space-y-3'>
              <h2 className='font-semibold text-foreground'>Resources</h2>
              <ul className='space-y-2'>
                <li>
                  <Link
                    href='https://www.curietech.ai/security'
                    target='_blank'
                    className='text-sm hover:text-foreground transition-colors'
                  >
                    Security
                  </Link>
                </li>
                <li>
                  <Link
                    href='https://trust.curietech.ai/'
                    target='_blank'
                    className='text-sm hover:text-foreground transition-colors'
                  >
                    Trust Center
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div className='space-y-3'>
              <h2 className='font-semibold text-foreground'>Company</h2>
              <ul className='space-y-2'>
                <li>
                  <Link
                    href='https://www.curietech.ai/about'
                    className='text-sm hover:text-foreground transition-colors'
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    href='https://www.curietech.ai/careers'
                    className='text-sm hover:text-foreground transition-colors'
                  >
                    Careers
                  </Link>
                </li>
                <li>
                  <Link
                    href='https://www.curietech.ai/contact-us'
                    className='text-sm hover:text-foreground transition-colors'
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className='flex flex-col sm:flex-row justify-between items-center py-6 border-t border-border'>
          <div className='text-sm'>
            © {new Date().getFullYear()} CurieTech, Inc. All rights reserved.
          </div>
          <div className='flex gap-4 mt-4 sm:mt-0 pr-12'>
            <Link href='/privacy' className='text-sm hover:text-foreground transition-colors'>
              Privacy Policy
            </Link>
            <Link href='/terms' className='text-sm hover:text-foreground transition-colors'>
              Terms and Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
